import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './AboutMe.css';

/**
 * Hidden page — reachable only at /aboutme, not linked from anywhere.
 * Same content as /aboutme, rebuilt as a mobile-first, swipeable card deck
 * (drag / arrow keys / tap the edges to move between cards).
 */

const RotatingImage: React.FC<{ images: string[]; alt: string }> = ({
  images,
  alt,
}) => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (images.length < 2) return;
    const id = window.setInterval(
      () => setIdx((i) => (i + 1) % images.length),
      2000
    );
    return () => window.clearInterval(id);
  }, [images.length]);

  return <img src={images[idx]} alt={alt} loading="lazy" />;
};

/** Where I've worked — official logos, files in /images/logos/. */
const orgs = [
  { file: 'arm.svg', name: 'ARM', cls: 'is-glyph' },
  { file: 'amazon.svg', name: 'Amazon', cls: 'is-glyph' },
  { file: 'ucl.svg', name: 'UCL', cls: 'is-word' },
  { file: 'kinisi.svg', name: 'Kinisi', cls: 'is-word' },
  { file: 'bear.png', name: 'Bear Robotics', cls: 'is-word' },
];

const LogoStrip: React.FC = () => (
  <div className="a2logos">
    {orgs.map((o) => (
      <img
        key={o.file}
        className={`a2logos__logo ${o.cls}`}
        src={`/images/logos/${o.file}`}
        alt={o.name}
        loading="lazy"
      />
    ))}
  </div>
);

interface ThemeItem {
  n: string;
  title: string;
  body: React.ReactNode;
}

const themeData: ThemeItem[] = [
  {
    n: '01',
    title: 'Reshoring',
    body: (
      <>
        Europe and the US will keep pulling strategic supply chains home. Combine
        that with aging workforces and you get the strongest case for automation
        in <mark>manufacturing</mark>, <mark>construction</mark>,{' '}
        <mark>infra</mark>, <mark>retraining</mark>.
      </>
    ),
  },
  {
    n: '02',
    title: 'Resource-maxxing',
    body: (
      <>
        New technology is unlocking better ways to produce and recover resources:{' '}
        <mark>automated vertical farms</mark>, <mark>intelligent fields</mark> and{' '}
        <mark>aquaponics</mark>; smarter energy infrastructure, from{' '}
        <mark>turbine maintenance</mark> to{' '}
        <mark>clearing weeds around solar panels</mark> and{' '}
        <mark>predicting defects early</mark>; and reclaiming metals through{' '}
        <mark>urban mining</mark> and <mark>EV-battery recycling</mark>.
      </>
    ),
  },
  {
    n: '03',
    title: 'Agentification of Physical AI',
    body: (
      <>
        <mark>Scientific experiment farms</mark>. Automation as simple as showing
        what you want once. Robot fleets commanded and observed as seamless
        skills. <mark>Evaluation-as-a-service</mark> for experimentation and
        CI/CD. <mark>Inspection, fully automated</mark>.
      </>
    ),
  },
];

/** Startups I find interesting — external links. */
const bonusOrgs = [
  { name: 'Skild AI', url: 'skild.ai' },
  { name: 'Reimagine Robotics', url: 'reimaginerobotics.ai' },
  { name: 'Dream Machines', url: 'dreammachines.eu' },
  { name: 'OpenVector', url: 'openvector.com' },
  { name: 'Machina Labs', url: 'machinalabs.ai' },
  { name: 'Varda', url: 'varda.com' },
  { name: 'Recursive', url: 'recursive.com' },
  { name: 'Odyssey', url: 'odyssey.ml' },
  { name: 'Jack & Jill', url: 'jackandjill.ai' },
  { name: 'Rembrand AI', url: 'rembrand.com' },
  { name: 'Antioch', url: 'antioch.com' },
];

interface Card {
  label: string;
  content: React.ReactNode;
}

const cards: Card[] = [
  {
    label: 'Intro',
    content: (
      <div className="a2card__pad a2card__center">
        <span className="a2card__eyebrow">About me</span>
        <p className="a2card__tldr">
          <strong>TL;DR:</strong> Expert in robotics and computer vision,
          interested in reshoring, resource maximisation, and agentification,
          among other themes.
        </p>
        <span className="a2card__hint">swipe →</span>
      </div>
    ),
  },
  {
    label: 'Background',
    content: (
      <div className="a2card__pad">
        <p>
          For the last few years I&apos;ve taught robots to{' '}
          <mark>pick, sort and adapt to objects they haven&apos;t seen</mark>.
          Sometimes the robot learns a task from one demonstration, sometimes off
          a large dataset, sometimes by trial and error.
        </p>
        <p>
          I&apos;ve owned work across{' '}
          <mark>perception, robot learning and deployment, data infrastructure,
          teleoperation</mark>, in academia, big tech and startups alike. Before
          working with robots I designed vision hardware at ARM and
          face-recognition software for healthcare.<sup>*</sup>
        </p>
        <p className="a2card__note">
          <sup>*</sup> Fun fact: actually, at 17 my first job was selling fruits
          I never saw, connecting sellers and buyers across the world for a
          commission.
        </p>
        <LogoStrip />
      </div>
    ),
  },
  {
    label: 'Project',
    content: (
      <div className="a2card__project">
        <div className="a2card__img">
          <RotatingImage
            images={['/images/kinisi-hero.jpg']}
            alt="Kinisi warehouse humanoid"
          />
        </div>
        <div className="a2card__pad">
          <span className="a2card__eyebrow">Kinisi Robotics / Bear Robotics</span>
          <p>
            Teaching a wheeled humanoid to pick, place and sort in real
            warehouses.
          </p>
        </div>
      </div>
    ),
  },
  {
    label: 'Project',
    content: (
      <div className="a2card__project">
        <div className="a2card__img">
          <RotatingImage
            images={['/images/amazon-vulcan.jpg']}
            alt="Amazon Robotics picking"
          />
        </div>
        <div className="a2card__pad">
          <span className="a2card__eyebrow">Amazon Robotics</span>
          <p>
            Built the vision that lets a robot identify and pick items packed
            tight in a bin, with barely any usable view of them. It cut
            identification failures by more than half.
          </p>
        </div>
      </div>
    ),
  },
  {
    label: 'Project',
    content: (
      <div className="a2card__project">
        <div className="a2card__img">
          <RotatingImage
            images={['/images/icra25-before.png', '/images/icra25-after.png']}
            alt="Learning from a single demonstration"
          />
        </div>
        <div className="a2card__pad">
          <span className="a2card__eyebrow">UCL</span>
          <p>
            Show the robot a task once and it repeats the skill on new objects it
            has never seen.
          </p>
        </div>
      </div>
    ),
  },
  {
    label: 'Project',
    content: (
      <div className="a2card__project">
        <div className="a2card__img">
          <RotatingImage
            images={['/images/stepp.png']}
            alt="Semantic traversability estimation"
          />
        </div>
        <div className="a2card__pad">
          <span className="a2card__eyebrow">UCL</span>
          <p>
            I&apos;ve done semantic scene understanding just about every way it
            comes: segmentation, detection, recognition, 3D reconstruction,
            grounding, traversability estimation.
          </p>
        </div>
      </div>
    ),
  },
  {
    label: 'Themes',
    content: (
      <div className="a2card__pad">
        <span className="a2card__eyebrow">Themes I&apos;ve been thinking about</span>
        <div className="a2themes">
          {themeData.map((t) => (
            <details className="a2theme" key={t.n}>
              <summary>
                <span className="a2theme__num">{t.n}</span>
                <span className="a2theme__title">{t.title}</span>
                <span className="a2theme__plus" aria-hidden="true">
                  +
                </span>
              </summary>
              <p>{t.body}</p>
            </details>
          ))}
        </div>
        <p className="a2card__note">
          Not an exhaustive list. I&apos;m curious about a lot: climate
          resilience, aging populations, the drift toward isolationism, and new
          distribution surfaces (e.g. for marketing).
        </p>
      </div>
    ),
  },
  {
    label: 'Links',
    content: (
      <div className="a2card__pad a2card__center">
        <p className="a2card__backlink">
          <Link to="/">← Back to the home page for the full list of projects &amp; publications</Link>
        </p>
        <div className="a2card__foot">
          <a
            href="https://www.linkedin.com/in/denishadjivelichkov/"
            target="_blank"
            rel="noopener noreferrer"
          >
            LinkedIn
          </a>
          <a
            href="https://scholar.google.com/citations?hl=en&user=pFSbKWMAAAAJ"
            target="_blank"
            rel="noopener noreferrer"
          >
            Scholar
          </a>
          <a href="mailto:dennis.hvel@gmail.com">Email</a>
          <a href="/CV.pdf" target="_blank" rel="noopener noreferrer">
            CV
          </a>
        </div>
      </div>
    ),
  },
  {
    label: 'Bonus',
    content: (
      <div className="a2card__pad">
        <span className="a2card__eyebrow">Bonus</span>
        <p>Startups I find interesting and exciting right now.</p>
        <ul className="a2bonus">
          {bonusOrgs.map((b) => (
            <li key={b.url}>
              <a
                href={`https://${b.url}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {b.name}
              </a>
              <span className="a2bonus__url">{b.url}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
];

const AboutMe: React.FC = () => {
  const [i, setI] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const iRef = useRef(0);
  iRef.current = i;

  // Native horizontal scroll-snap does the swiping — the browser handles
  // touch, momentum and snapping, which is the only thing that behaves
  // reliably on mobile Safari. We just mirror the scroll position into
  // the dots / counter, and drive it from dots + arrow keys.
  const scrollToCard = useCallback((n: number, smooth = true) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(cards.length - 1, n));
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const n = Math.round(track.scrollLeft / track.clientWidth);
        if (n !== iRef.current) setI(n);
      });
    };
    track.addEventListener('scroll', onScroll, { passive: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') scrollToCard(iRef.current + 1);
      if (e.key === 'ArrowLeft') scrollToCard(iRef.current - 1);
    };
    window.addEventListener('keydown', onKey);

    // keep the active card aligned if the viewport resizes
    const onResize = () => scrollToCard(iRef.current, false);
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [scrollToCard]);

  return (
    <div className="aboutme">
      <div className="aboutme__stage">
        <button
          className="aboutme__edge aboutme__edge--l"
          aria-label="Previous card"
          onClick={() => scrollToCard(i - 1)}
          disabled={i === 0}
        >
          ‹
        </button>

        <div className="aboutme__track" ref={trackRef}>
          {cards.map((c, n) => (
            <div className="aboutme__slide" key={n}>
              <div className="a2card">{c.content}</div>
            </div>
          ))}
        </div>

        <button
          className="aboutme__edge aboutme__edge--r"
          aria-label="Next card"
          onClick={() => scrollToCard(i + 1)}
          disabled={i === cards.length - 1}
        >
          ›
        </button>
      </div>

      <div className="aboutme__dots">
        {cards.map((c, n) => (
          <button
            key={n}
            className={`aboutme__dot${n === i ? ' is-active' : ''}`}
            aria-label={`Card ${n + 1}: ${c.label}`}
            onClick={() => scrollToCard(n)}
          />
        ))}
      </div>
    </div>
  );
};

export default AboutMe;
